import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminHeader from '../../components/admin/AdminHeader';
import { buildApiUrl } from '../../config/api';
import '../../styles/admin-newsletter.css';

const AdminNewsletter = () => {
    const [subscribers, setSubscribers] = useState([]);
    const [stats, setStats] = useState({ total: 0, active: 0, unsubscribed: 0 });
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [filters, setFilters] = useState({
        search: '',
        status: 'all'
    });

    const getHeaders = () => {
        const token = localStorage.getItem('token');
        return {
            Authorization: `Bearer ${token}`,
            'x-auth-token': token
        };
    };

    const fetchNewsletterData = useCallback(async () => {
        try {
            setLoading(true);

            const headers = getHeaders();
            const [statsResponse, subscribersResponse] = await Promise.all([
                axios.get(buildApiUrl('/contact/newsletter/stats'), { headers }),
                axios.get(buildApiUrl('/contact/newsletter/subscribers'), { headers })
            ]);

            setStats(statsResponse.data?.data || { total: 0, active: 0, unsubscribed: 0 });
            setSubscribers(subscribersResponse.data?.data || []);
        } catch (error) {
            console.error('Error fetching newsletter data:', error);
            alert(error.response?.data?.message || 'Failed to load newsletter data');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchNewsletterData();
    }, [fetchNewsletterData]);

    const filteredSubscribers = useMemo(() => {
        const query = filters.search.trim().toLowerCase();

        return subscribers.filter((subscriber) => {
            const statusMatch = filters.status === 'all' || subscriber.status === filters.status;
            const searchMatch = !query || subscriber.email?.toLowerCase().includes(query);
            return statusMatch && searchMatch;
        });
    }, [filters, subscribers]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleStatusUpdate = async (subscriber, status) => {
        try {
            setUpdating(true);
            await axios.patch(
                buildApiUrl(`/contact/newsletter/subscribers/${subscriber.id}/status`),
                { status },
                { headers: getHeaders() }
            );

            setSubscribers((prev) =>
                prev.map((item) => (item.id === subscriber.id ? { ...item, status } : item))
            );
            setStats((prev) => {
                const next = { ...prev };
                if (subscriber.status === 'active') next.active -= 1;
                if (subscriber.status === 'unsubscribed') next.unsubscribed -= 1;
                if (status === 'active') next.active += 1;
                if (status === 'unsubscribed') next.unsubscribed += 1;
                return next;
            });
        } catch (error) {
            console.error('Error updating newsletter subscriber:', error);
            alert(error.response?.data?.message || 'Failed to update subscriber');
        } finally {
            setUpdating(false);
        }
    };

    const handleDelete = async (subscriber) => {
        if (!window.confirm(`Delete subscriber ${subscriber.email}?`)) {
            return;
        }

        try {
            setUpdating(true);
            await axios.delete(
                buildApiUrl(`/contact/newsletter/subscribers/${subscriber.id}`),
                { headers: getHeaders() }
            );

            setSubscribers((prev) => prev.filter((item) => item.id !== subscriber.id));
            setStats((prev) => ({
                total: Math.max(prev.total - 1, 0),
                active: subscriber.status === 'active' ? Math.max(prev.active - 1, 0) : prev.active,
                unsubscribed: subscriber.status === 'unsubscribed'
                    ? Math.max(prev.unsubscribed - 1, 0)
                    : prev.unsubscribed
            }));
        } catch (error) {
            console.error('Error deleting newsletter subscriber:', error);
            alert(error.response?.data?.message || 'Failed to delete subscriber');
        } finally {
            setUpdating(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    return (
        <div className="admin-layout">
            <AdminSidebar />
            <div className="admin-main newsletter-admin-page">
                <AdminHeader
                    title="Newsletter Management"
                    subtitle="Track subscribers and manage newsletter audience"
                />

                <section className="newsletter-hero-card">
                    <div className="newsletter-hero-copy">
                        <span className="newsletter-hero-eyebrow">Audience Center</span>
                        <h2>Manage your newsletter audience from one clean workspace</h2>
                        <p>
                            Monitor active subscribers, reactivate users, and keep your mailing list organized
                            without leaving the admin panel.
                        </p>
                    </div>
                    <div className="newsletter-hero-metrics">
                        <div className="newsletter-hero-pill">
                            <i className="fas fa-envelope-open-text"></i>
                            <span>{stats.active} active readers</span>
                        </div>
                        <div className="newsletter-hero-pill">
                            <i className="fas fa-user-clock"></i>
                            <span>{stats.unsubscribed} paused contacts</span>
                        </div>
                    </div>
                </section>

                <div className="stats-grid">
                    <div className="stat-card newsletter-stat-card total">
                        <div className="stat-icon">
                            <i className="fas fa-users"></i>
                        </div>
                        <div className="stat-details">
                            <span className="stat-label">Total Subscribers</span>
                            <span className="stat-value">{stats.total}</span>
                        </div>
                    </div>
                    <div className="stat-card newsletter-stat-card active">
                        <div className="stat-icon">
                            <i className="fas fa-envelope-open-text"></i>
                        </div>
                        <div className="stat-details">
                            <span className="stat-label">Active</span>
                            <span className="stat-value">{stats.active}</span>
                        </div>
                    </div>
                    <div className="stat-card newsletter-stat-card unsubscribed">
                        <div className="stat-icon">
                            <i className="fas fa-user-minus"></i>
                        </div>
                        <div className="stat-details">
                            <span className="stat-label">Unsubscribed</span>
                            <span className="stat-value">{stats.unsubscribed}</span>
                        </div>
                    </div>
                </div>

                <div className="filters-card newsletter-filters-card">
                    <div className="filters-header">
                        <h3>
                            <i className="fas fa-filter"></i>
                            Newsletter Filters
                        </h3>
                    </div>
                    <div className="filters-body">
                        <div className="filters-grid">
                            <div className="filter-group">
                                <label>Search Email</label>
                                <input
                                    type="text"
                                    name="search"
                                    value={filters.search}
                                    onChange={handleFilterChange}
                                    placeholder="Search by email"
                                />
                            </div>
                            <div className="filter-group">
                                <label>Status</label>
                                <select name="status" value={filters.status} onChange={handleFilterChange}>
                                    <option value="all">All</option>
                                    <option value="active">Active</option>
                                    <option value="unsubscribed">Unsubscribed</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="content-header newsletter-content-header">
                    <div className="results-info">
                        <i className="fas fa-newspaper"></i>
                        <span>Showing <strong>{filteredSubscribers.length}</strong> of <strong>{subscribers.length}</strong> subscribers</span>
                    </div>
                    <button className="toolbar-refresh newsletter-refresh-btn" type="button" onClick={fetchNewsletterData}>
                        <i className="fas fa-rotate-right"></i>
                        Refresh
                    </button>
                </div>

                <div className="admin-content">
                    {loading ? (
                        <div className="loading-state">
                            <div className="spinner"></div>
                            <p>Loading subscribers...</p>
                        </div>
                    ) : (
                        <div className="table-container newsletter-table-shell">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Email</th>
                                        <th>Status</th>
                                        <th>Subscribed On</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredSubscribers.length > 0 ? (
                                        filteredSubscribers.map((subscriber) => (
                                            <tr key={subscriber.id}>
                                                <td>
                                                    <div className="subscriber-email-cell">
                                                        <span className="subscriber-avatar">
                                                            {subscriber.email?.charAt(0)?.toUpperCase() || 'N'}
                                                        </span>
                                                        <div className="subscriber-copy">
                                                            <strong>{subscriber.email}</strong>
                                                            <span>Newsletter subscriber</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className={`status-badge ${subscriber.status} newsletter-status-badge`}>
                                                        {subscriber.status}
                                                    </span>
                                                </td>
                                                <td>{formatDate(subscriber.created_at)}</td>
                                                <td className="actions-cell">
                                                    {subscriber.status === 'active' ? (
                                                        <button
                                                            type="button"
                                                            className="action-btn delete"
                                                            disabled={updating}
                                                            onClick={() => handleStatusUpdate(subscriber, 'unsubscribed')}
                                                            title="Unsubscribe"
                                                        >
                                                            <i className="fas fa-user-minus"></i>
                                                        </button>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="action-btn edit"
                                                            disabled={updating}
                                                            onClick={() => handleStatusUpdate(subscriber, 'active')}
                                                            title="Activate"
                                                        >
                                                            <i className="fas fa-user-check"></i>
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="action-btn delete"
                                                        disabled={updating}
                                                        onClick={() => handleDelete(subscriber)}
                                                        title="Delete"
                                                    >
                                                        <i className="fas fa-trash"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="4" className="text-center">
                                                No subscribers found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminNewsletter;
